import React from 'react';
import { CaseAttachmentViewerModal } from './CaseAttachmentViewerModal';

const CASE_BRAND_TEXT = '#123F43';
const CASE_BRAND_MUTED = '#64716F';
const CASE_BRAND_BORDER = '#DDD4C8';
const CASE_BRAND_CARD = '#FFFDF9';
const CASE_BRAND_PANEL = '#FBF8F2';
const CASE_BRAND_TEXT_TINT = 'rgba(18, 63, 67, 0.08)';
const CASE_BRAND_TEXT_TINT_BORDER = 'rgba(18, 63, 67, 0.18)';
const CASE_BRAND_SUCCESS_BG = '#E7F2EC';
const CASE_BRAND_SUCCESS_BORDER = '#BDD4C7';
const CASE_BRAND_SUCCESS_TEXT = '#25624D';

type Props = {
  caseDetailModal: any | null;
  styles: any;
  closeCaseDetailModal: () => void;
  getCaseDisplayNumber: (caseItem: any) => string;
  needsModelVerification: (caseData: any) => boolean;
  getCustomModelInfo: (caseData: any) => any;
  getPendingDeviceInfo: (caseData: any) => any;
  canVerifySubmittedDevices: boolean;
  verifyModel: (action: 'approve' | 'edit' | 'reject') => void | Promise<void>;
  setEditingModel: React.Dispatch<React.SetStateAction<{
    modelNumber: string;
    manufacturer: string;
    serialNumber: string;
    assetNumber: string;
    deviceId: string;
  }>>;
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  baseUrl: string;
  resolveMediaCategory: (media: any) => string;
  modalCaseAnalysisMeta: any;
  caseDetailLoading: boolean;
  caseQuotesLoading: boolean;
  modalCaseQuotes: any[];
  modalCaseServiceReports: any[];
  modalCaseSurveys: any[];
  caseServiceReportPreviewLoadingId: string | null;
  getQuoteDecisionBadge: (quote: any) => { label: string; style: any };
  getQuotePartsBadge: (quote: any) => { label: string; detail?: string; style: any } | null;
  setCaseQuotePreview: React.Dispatch<React.SetStateAction<any>>;
  setCaseQuotePreviewContext: React.Dispatch<React.SetStateAction<any>>;
  openCaseServiceReportPreview: (report: any, contextCase: any) => void | Promise<void>;
  modalTimelineEntries: any[];
  formatTimelineEventLabel: (event: any) => string;
};

function resolveCaseDetailMediaUrl(baseUrl: string, fileUrl: string | null | undefined) {
  const raw = String(fileUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalizedBase = String(baseUrl || '').replace(/\/$/, '');
  if (!normalizedBase) return raw;
  return `${normalizedBase}${raw.startsWith('/') ? raw : `/${raw}`}`;
}

export const TechDashboardCaseDetailModal = React.memo(function TechDashboardCaseDetailModal({
  caseDetailModal,
  styles,
  closeCaseDetailModal,
  getCaseDisplayNumber,
  needsModelVerification,
  getCustomModelInfo,
  getPendingDeviceInfo,
  canVerifySubmittedDevices,
  verifyModel,
  setEditingModel,
  setShowEditModal,
  baseUrl,
  resolveMediaCategory,
  modalCaseAnalysisMeta,
  caseDetailLoading,
  caseQuotesLoading,
  modalCaseQuotes,
  modalCaseServiceReports,
  modalCaseSurveys,
  caseServiceReportPreviewLoadingId,
  getQuoteDecisionBadge,
  getQuotePartsBadge,
  setCaseQuotePreview,
  setCaseQuotePreviewContext,
  openCaseServiceReportPreview,
  modalTimelineEntries,
  formatTimelineEventLabel
}: Props) {
  const [caseAttachmentViewer, setCaseAttachmentViewer] = React.useState<any | null>(null);

  React.useEffect(() => {
    setCaseAttachmentViewer(null);
  }, [caseDetailModal?.id]);

  if (!caseDetailModal) return null;
  const pendingDeviceInfo = getPendingDeviceInfo(caseDetailModal);
  const customModelInfo = getCustomModelInfo(caseDetailModal);
  const verificationIsDevice = Boolean(pendingDeviceInfo);
  const deviceApprovalRequiresEdit = verificationIsDevice && Boolean(pendingDeviceInfo?.temporarySerialNumber);

  const modalContentStyle = {
    ...styles.modalContentWide,
    width: 'min(1080px, 94vw)',
    maxHeight: 'calc(100vh - 2rem)',
    backgroundColor: CASE_BRAND_CARD,
    border: `1px solid ${CASE_BRAND_BORDER}`,
    borderRadius: '0.85rem',
    boxShadow: '0 24px 40px rgba(18, 63, 67, 0.18)'
  };
  const modalHeaderStyle = {
    ...styles.modalHeader,
    borderBottom: `1px solid ${CASE_BRAND_BORDER}`,
    paddingBottom: '0.75rem',
    marginBottom: '1rem'
  };
  const modalTitleStyle = {
    ...styles.modalTitle,
    color: CASE_BRAND_TEXT,
    fontWeight: 800
  };
  const closeButtonStyle = {
    border: `1px solid ${CASE_BRAND_BORDER}`,
    backgroundColor: CASE_BRAND_CARD,
    color: CASE_BRAND_TEXT,
    borderRadius: '0.65rem',
    padding: '0.42rem 0.8rem',
    fontWeight: 600,
    fontSize: '0.95rem',
    cursor: 'pointer'
  };
  const infoSectionStyle = {
    ...styles.infoSection,
    border: `1px solid ${CASE_BRAND_BORDER}`,
    backgroundColor: CASE_BRAND_PANEL,
    borderRadius: '0.85rem',
    padding: '0.9rem 1rem',
    marginBottom: '0.9rem'
  };
  const labelStyle = {
    ...styles.label,
    color: CASE_BRAND_MUTED,
    fontSize: '0.76rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    fontWeight: 800
  };
  const valueStyle = {
    ...styles.value,
    color: CASE_BRAND_TEXT
  };

  return (
    <div style={styles.modal} onClick={closeCaseDetailModal}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h2 style={modalTitleStyle}>Case #{getCaseDisplayNumber(caseDetailModal)}</h2>
          <button style={closeButtonStyle} onClick={closeCaseDetailModal}>Close</button>
        </div>

        {needsModelVerification(caseDetailModal) && (
          <div style={styles.warningBox}>
            <div style={styles.warningTitle}>
              {verificationIsDevice ? 'Device Verification Required' : 'Model Verification Required'}
            </div>
            <div style={styles.warningText}>
              {verificationIsDevice ? (
                <>
                  Customer submitted an unregistered device:
                  {' '}
                  {pendingDeviceInfo?.assetNumber ? `Asset ${pendingDeviceInfo.assetNumber} | ` : ''}
                  Serial {pendingDeviceInfo?.serialNumber || caseDetailModal.device?.serialNumber || 'not provided'}.
                  {pendingDeviceInfo?.temporarySerialNumber ? ` Temporary serial: ${pendingDeviceInfo.temporarySerialNumber}.` : ''}
                  {deviceApprovalRequiresEdit ? ' Use Edit Device & Approve to confirm the real serial before approval.' : ''}
                </>
              ) : (
                <>
                  Customer entered a custom model: {customModelInfo?.modelNumber}
                  ({customModelInfo?.manufacturer})
                </>
              )}
            </div>
            {canVerifySubmittedDevices ? (
              <div style={styles.verificationButtons}>
                <button
                  style={{
                    ...styles.approveButton,
                    ...(deviceApprovalRequiresEdit ? { opacity: 0.55, cursor: 'not-allowed' } : {})
                  }}
                  onClick={() => verifyModel('approve')}
                  disabled={deviceApprovalRequiresEdit}
                >
                  {verificationIsDevice ? 'Approve Device' : 'Approve Model'}
                </button>
                <button style={styles.editButton} onClick={() => {
                  const info = verificationIsDevice ? pendingDeviceInfo : customModelInfo;
                  setEditingModel({
                    modelNumber: info?.modelNumber || caseDetailModal.device?.modelNumber || '',
                    manufacturer: info?.manufacturer || caseDetailModal.device?.manufacturer || '',
                    serialNumber:
                      info?.serialNumber ||
                      (pendingDeviceInfo?.temporarySerialNumber ? '' : caseDetailModal.device?.serialNumber) ||
                      '',
                    assetNumber: info?.assetNumber || caseDetailModal.device?.assetNumber || '',
                    deviceId: ''
                  });
                  setShowEditModal(true);
                }}>
                  {verificationIsDevice ? 'Edit Device & Approve' : 'Edit & Approve'}
                </button>
                <button style={styles.rejectButton} onClick={() => verifyModel('reject')}>
                  {verificationIsDevice ? 'Reject Device' : 'Reject Model'}
                </button>
              </div>
            ) : (
              <div style={{ ...styles.warningText, marginTop: '0.75rem', fontWeight: 700 }}>
                Admin or superuser verification is required before this case can proceed.
              </div>
            )}
          </div>
        )}

        <div style={infoSectionStyle}>
          <label style={labelStyle}>Customer</label>
          <div style={valueStyle}>{caseDetailModal.customer?.companyName}</div>
        </div>

        <div style={infoSectionStyle}>
          <label style={labelStyle}>Device</label>
          <div style={valueStyle}>
            {caseDetailModal.device?.modelNumber} - {caseDetailModal.device?.serialNumber}
          </div>
        </div>

        <div style={infoSectionStyle}>
          <label style={labelStyle}>Problem Description</label>
          <div style={valueStyle}>{caseDetailModal.problemDescription}</div>
        </div>

        <div style={infoSectionStyle}>
          <label style={labelStyle}>Key Details</label>
          <div style={styles.keyValueGrid}>
            <div style={styles.keyValueItem}>
              <span style={styles.keyLabel}>Case ID</span>
              <span style={styles.keyValue}>{caseDetailModal.id}</span>
            </div>
            <div style={styles.keyValueItem}>
              <span style={styles.keyLabel}>Case Code</span>
              <span style={styles.keyValue}>{caseDetailModal.caseCode ?? 'N/A'}</span>
            </div>
            <div style={styles.keyValueItem}>
              <span style={styles.keyLabel}>Created</span>
              <span style={styles.keyValue}>{caseDetailModal.createdAt ? new Date(caseDetailModal.createdAt).toLocaleString() : 'N/A'}</span>
            </div>
            <div style={styles.keyValueItem}>
              <span style={styles.keyLabel}>Updated</span>
              <span style={styles.keyValue}>{caseDetailModal.updatedAt ? new Date(caseDetailModal.updatedAt).toLocaleString() : 'N/A'}</span>
            </div>
            <div style={styles.keyValueItem}>
              <span style={styles.keyLabel}>Opened</span>
              <span style={styles.keyValue}>{caseDetailModal.openedAt ? new Date(caseDetailModal.openedAt).toLocaleString() : 'N/A'}</span>
            </div>
            <div style={styles.keyValueItem}>
              <span style={styles.keyLabel}>Closed</span>
              <span style={styles.keyValue}>{caseDetailModal.closedAt ? new Date(caseDetailModal.closedAt).toLocaleString() : 'N/A'}</span>
            </div>
          </div>
        </div>

        {caseDetailModal.customerInputs && caseDetailModal.customerInputs.length > 0 && (
          <div style={infoSectionStyle}>
            <label style={labelStyle}>Customer Responses</label>
            {caseDetailModal.customerInputs.map((input: any, index: number) => (
              <div key={index} style={{ marginBottom: '0.5rem' }}>
                <strong>{input.question}:</strong> {input.answer}
              </div>
            ))}
          </div>
        )}

        {caseDetailModal.media && caseDetailModal.media.length > 0 && (
          <div style={infoSectionStyle}>
            <label style={labelStyle}>Attached Media</label>
            <div style={styles.mediaGrid}>
              {caseDetailModal.media.map((media: any, index: number) => {
                const url = resolveCaseDetailMediaUrl(baseUrl, media.fileUrl);
                const category = resolveMediaCategory(media);
                return (
                  <div key={`${media.id || index}`} style={styles.mediaCard}>
                    {category === 'image' && (
                      <img src={url} alt={media.fileName || 'Attachment'} style={styles.mediaPreview} />
                    )}
                    {category === 'video' && (
                      <video src={url} controls style={styles.mediaPreview} />
                    )}
                    {category === 'audio' && (
                      <audio src={url} controls style={styles.mediaPreview} />
                    )}
                    {category === 'file' && (
                      <div style={{ ...styles.mediaPreview, padding: '0.75rem', fontSize: '0.85rem' }}>
                        File attached
                      </div>
                    )}
                    <div style={styles.mediaFileName}>{media.fileName || media.fileType || 'Attachment'}</div>
                    <div style={styles.mediaMeta}>
                      {media.fileType} | {(media.fileSize / 1024).toFixed(1)} KB
                    </div>
                    <button
                      type="button"
                      style={{
                        ...styles.mediaLink,
                        border: 0,
                        background: 'transparent',
                        padding: 0,
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                      onClick={() => setCaseAttachmentViewer({
                        ...media,
                        url,
                        fileName: media.fileName || media.fileType || `Attachment ${index + 1}`,
                        mimeType: media.mimeType || null,
                        fileType: media.fileType || null,
                        fileSize: media.fileSize || null
                      })}
                    >
                      Open
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {modalCaseAnalysisMeta && (
          <div style={infoSectionStyle}>
            <label style={labelStyle}>TSFAD Status</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
              <span
                style={{
                  ...styles.analysisBadge,
                  ...(modalCaseAnalysisMeta.variant === 'accepted'
                    ? styles.analysisBadgeAccepted
                    : modalCaseAnalysisMeta.variant === 'pending'
                    ? styles.analysisBadgePending
                    : styles.analysisBadgeIdle)
                }}
              >
                {modalCaseAnalysisMeta.label}
              </span>
              {modalCaseAnalysisMeta.detail && (
                <span style={styles.analysisMetaRow}>{modalCaseAnalysisMeta.detail}</span>
              )}
            </div>
          </div>
        )}

        {caseDetailLoading && (
          <div style={{ fontSize: '0.85rem', color: CASE_BRAND_MUTED, margin: '0.5rem 0' }}>
            Loading latest case activity...
          </div>
        )}

        {Array.isArray(caseDetailModal.tasks) && caseDetailModal.tasks.length > 0 && (
          <div style={infoSectionStyle}>
            <label style={labelStyle}>Tasks</label>
            <div style={styles.taskList}>
              {caseDetailModal.tasks.map((task: any) => (
                (() => {
                  const assigneeNames = Array.isArray(task?.assignees)
                    ? task.assignees
                        .map((entry: any) => String(entry?.fullName || entry?.username || '').trim())
                        .filter(Boolean)
                    : [];
                  const assigneeLabel =
                    assigneeNames.length > 0
                      ? assigneeNames.join(', ')
                      : 'Unassigned';
                  return (
                    <div key={task.id} style={styles.taskCard}>
                      <div style={styles.taskHeader}>
                        <span>{task.taskCode ?? 'Task'} | {task.status ?? 'PENDING'}</span>
                        <span style={{ fontSize: '0.75rem', color: CASE_BRAND_MUTED }}>
                          {task.createdAt ? new Date(task.createdAt).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                      <div style={styles.taskMeta}>
                        Stage: {task.stage?.label ?? 'N/A'} | Assigned: {assigneeLabel}
                      </div>
                      {task.completedAt && (
                        <div style={styles.taskMeta}>
                          Completed: {new Date(task.completedAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  );
                })()
              ))}
            </div>
          </div>
        )}

        {caseQuotesLoading && (
          <div style={{ fontSize: '0.85rem', color: CASE_BRAND_MUTED, margin: '0.5rem 0' }}>
            Loading saved quotes...
          </div>
        )}

        {modalCaseQuotes.length > 0 && (
          <div style={infoSectionStyle}>
            <label style={labelStyle}>Saved Quotes ({modalCaseQuotes.length})</label>
            <div style={styles.quoteList}>
              {modalCaseQuotes.map((quote: any) => {
                const decisionBadge = getQuoteDecisionBadge(quote);
                const partsBadge = getQuotePartsBadge(quote);
                return (
                  <div key={quote.quoteId} style={styles.quoteCard}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {quote.title}
                        <span style={decisionBadge.style}>{decisionBadge.label}</span>
                      </div>
                      <div style={styles.quoteMeta}>
                        {quote.templateName || 'Quote'} | Total $
                        {Number(quote?.totals?.grandTotal || 0).toFixed(2)}
                      </div>
                      <div style={styles.quoteMeta}>
                        {quote.savedAt ? new Date(quote.savedAt).toLocaleString() : 'Saved'} |{' '}
                        {quote.actorName || 'System'}
                      </div>
                      {partsBadge && (
                        <div style={styles.quoteMeta}>
                          <span style={partsBadge.style}>{partsBadge.label}</span>
                          {partsBadge.detail ? (
                            <span style={{ marginLeft: '0.45rem' }}>{partsBadge.detail}</span>
                          ) : null}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      style={styles.quoteViewButton}
                      onClick={() => {
                        setCaseQuotePreview(quote);
                        setCaseQuotePreviewContext(caseDetailModal);
                      }}
                    >
                      View Quote
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={infoSectionStyle}>
          <label style={labelStyle}>Service Reports ({modalCaseServiceReports.length})</label>
          {modalCaseServiceReports.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: CASE_BRAND_MUTED }}>
              No service reports available yet.
            </div>
          ) : (
            <div style={styles.quoteList}>
              {modalCaseServiceReports.map((report: any) => {
                const reportStatus = String(report?.status || '').trim().toUpperCase();
                const statusStyle =
                  reportStatus === 'SENT'
                    ? {
                        background: CASE_BRAND_SUCCESS_BG,
                        color: CASE_BRAND_SUCCESS_TEXT,
                        border: `1px solid ${CASE_BRAND_SUCCESS_BORDER}`
                      }
                    : {
                        background: CASE_BRAND_TEXT_TINT,
                        color: CASE_BRAND_TEXT,
                        border: `1px solid ${CASE_BRAND_TEXT_TINT_BORDER}`
                      };
                const isOpening = caseServiceReportPreviewLoadingId === report.id;
                return (
                  <div key={report.id} style={styles.quoteCard}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {report.title || 'Service Report'}
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            borderRadius: '999px',
                            padding: '0.15rem 0.5rem',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            marginLeft: '0.5rem',
                            ...statusStyle
                          }}
                        >
                          {reportStatus === 'SENT' ? 'Sent' : 'Saved'}
                        </span>
                      </div>
                      <div style={styles.quoteMeta}>
                        {report.templateName || 'Service report'}
                        {report.savedAt
                          ? ` | ${new Date(report.savedAt).toLocaleString()}`
                          : ''}
                      </div>
                      {report.savedByName && (
                        <div style={styles.quoteMeta}>{report.savedByName}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      style={styles.quoteViewButton}
                      onClick={() => openCaseServiceReportPreview(report, caseDetailModal)}
                      disabled={isOpening}
                    >
                      {isOpening ? 'Opening...' : 'View Report'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={infoSectionStyle}>
          <label style={labelStyle}>Customer Survey ({modalCaseSurveys.length})</label>
          {modalCaseSurveys.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: CASE_BRAND_MUTED }}>
              No survey has been released for this case yet.
            </div>
          ) : (
            <div style={styles.quoteList}>
              {modalCaseSurveys.map((survey: any) => {
                const surveyStatus = String(survey?.status || '').trim().toUpperCase();
                const isCompleted = surveyStatus === 'COMPLETED';
                const statusStyle =
                  isCompleted
                    ? {
                        background: CASE_BRAND_SUCCESS_BG,
                        color: CASE_BRAND_SUCCESS_TEXT,
                        border: `1px solid ${CASE_BRAND_SUCCESS_BORDER}`
                      }
                    : {
                        background: CASE_BRAND_TEXT_TINT,
                        color: CASE_BRAND_TEXT,
                        border: `1px solid ${CASE_BRAND_TEXT_TINT_BORDER}`
                      };
                return (
                  <div key={survey.id} style={styles.quoteCard}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {survey.surveyName || 'Customer Satisfaction Survey'}
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            borderRadius: '999px',
                            padding: '0.15rem 0.5rem',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            marginLeft: '0.5rem',
                            ...statusStyle
                          }}
                        >
                          {isCompleted ? 'Completed' : 'Waiting'}
                        </span>
                      </div>
                      <div style={styles.quoteMeta}>
                        {isCompleted
                          ? `Submitted ${survey.completedAt ? new Date(survey.completedAt).toLocaleString() : 'recently'}`
                          : `Released ${survey.releasedAt ? new Date(survey.releasedAt).toLocaleString() : 'recently'}`}
                      </div>
                      <div style={styles.quoteMeta}>
                        {survey.technicianName || 'Support team'} | {survey.questionCount || 0} question
                        {Number(survey.questionCount || 0) === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div style={{ ...styles.quoteMeta, fontWeight: 700, color: CASE_BRAND_TEXT }}>
                      {isCompleted && survey.scorePossible
                        ? `Score ${survey.scoreEarned ?? 0}/${survey.scorePossible}`
                        : 'Awaiting customer feedback'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {modalTimelineEntries.length > 0 && (
          <div style={infoSectionStyle}>
            <label style={labelStyle}>Case Timeline</label>
            <div style={styles.timeline}>
              {modalTimelineEntries.map((event: any) => (
                <div key={event.id} style={styles.timelineItem}>
                  <span style={styles.timelineBullet}></span>
                  <div style={{ fontWeight: 600 }}>{formatTimelineEventLabel(event)}</div>
                  <div style={styles.timelineMeta}>
                    {event.actorName ?? 'System'} | {event.occurredAt ? new Date(event.occurredAt).toLocaleString() : 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <CaseAttachmentViewerModal
          attachment={caseAttachmentViewer}
          onClose={() => setCaseAttachmentViewer(null)}
        />
      </div>
    </div>
  );
});
